import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Settings,
  BarChart,
  Monitor,
  Menu,
  FileText,
  LogOut,
  Upload,
  SearchCheck,
  Search,
  ChevronRight,
  ChevronDown,
  TableProperties,
  FileSearch,
  User,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const nav = [
  {
    to: "/",
    label: "Dashboard",
    icon: LayoutDashboard,
    roles: ["dashboard"],
  },
  {
    to: "/upload",
    label: "Upload",
    icon: Upload,
    roles: ["upload"],
  },
  // { to: "/exception", label: "Exceptions", icon: List },
  // {
  //   to: "/reports",
  //   label: "Reports",
  //   icon: BarChart,
  //   roles: ["reports"],
  // },
  {
    to: "/aimatch",
    label: "AI Match",
    icon: Monitor,
    roles: ["ai_match"],
  },
  {
    to: "/auditlog",
    label: "Audit-log",
    icon: FileText,
    roles: ["audit-log"],
  },
  // { to: "/exceptions-submit", label: "Exceptions Approval", icon: FileText },
];

// Settings Submenu Items
const settingsItems = [
  // { to: "/basic", label: "Basic Setting" },
  { to: "/matchingrule", label: "Matching Rule" },
  { to: "/templates", label: "Templates", icon: TableProperties },
  { to: "/system-configuration", label: "System Configuration" },
  { to: "/general-ledger", label: "General Ledger" },
  { to: "/product-setup", label: "Product Setup" },
  { to: "/response-code-setup", label: "Response Code Setup" },
];

const investigateItems = [
  { to: "/channel-investigate", label: "Channel", roles: ["channels"] },
  { to: "/account-investigate", label: "Account", roles: ["accounts"] },
];

const reportsItems = [
  { to: "/general-reports", label: "General" },
  { to: "/journal-reports", label: "Journal" },
];

const systemAdministration = [
  { to: "/module-config", label: "Module Configuration" },
  { to: "/role-management", label: "Role Management" },
  { to: "/user-management", label: "User Management" },
];

// const exceptionsItems = [
//   { to: "/exception/channel", label: "Channels" },
//   { to: "/exception/account", label: "Accounts" },
// ];

const channelItems = [
  { to: "/recon/channel/atm", label: "ATM" },
  { to: "/recon/channel/pos", label: "POS" },
  {
    label: "Cards",
    children: [
      { to: "/recon/channel/cards/nfs", label: "NFS" },
      { to: "/recon/channel/cards/visa", label: "VISA" },
      { to: "/recon/channel/cards/master-card", label: "Master Card" },
    ],
  },
  { to: "/recon/channel/e-money", label: "E-Money" },
  { to: "/recon/channel/mobile-money", label: "Mobile Money" },
  { to: "/recon/channel/bill-payments", label: "Bill Payments" },
  { to: "/recon/channel/eft", label: "EFT" },
  { to: "/recon/channel/internet-banking", label: "Internet Banking" },
  { to: "/recon/channel/wallet", label: "Wallet" },
  { to: "/recon/channel/ecommerce", label: "Ecommerce" },
];

const accountItems = [
  { to: "/recon/accounts/gl", label: "GL" },
  { to: "/recon/accounts/nostro", label: "NOSTRO" },
  { to: "/recon/accounts/ddacc", label: "DDACC" },
  { to: "/recon/accounts/rtgs", label: "RTGS" },
];

export default function Sidebar({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (value: boolean) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const isInvestigateActive =
    location.pathname.startsWith("/channel-investigate") ||
    location.pathname.startsWith("/account-investigate");

  const isReportsActive =
    location.pathname.startsWith("/general-reports") ||
    location.pathname.startsWith("/journal-reports");

  const isReconActive = location.pathname.startsWith("/recon");
  const isSettingsActive = location.pathname.startsWith("/settings");
  const [investigateOpen, setInvestigateOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("");

  const [reconOpen, setReconOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [channelOpen, setChannelOpen] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [systemAdminOpen, setSystemAdminOpen] = useState(false);
  const [userRole, setUserRole] = useState([]);

  const { user, logout, roleDetails } = useAuth();

  const toggleMenu = (label: string) => {
    setOpenMenu(openMenu === label ? null : label);
  };

  useEffect(() => {
    setReconOpen(false);
    const isSettingsRoute =
      location.pathname === "/basic" ||
      location.pathname === "/user" ||
      location.pathname === "/matchingrule";

    if (!isSettingsRoute) {
      setSettingsOpen(false);
    }

    setChannelOpen(false);
    setAccountsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const sidebar = document.querySelector("aside");
      if (sidebar && !sidebar.contains(e.target as Node)) {
        setInvestigateOpen(false);
        setChannelOpen(false);
        setAccountsOpen(false);
        setSettingsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Load user info
  useEffect(() => {
    const email = localStorage.getItem("userEmail") || "";
    setUserEmail(email);
    setUserName(email.split("@")[0]);

    let userRoleDetails = localStorage.getItem("user_role_details");
    if (!userRoleDetails) {
      throw new Error("user_role_details not found in localStorage");
    }
    let userDetails = JSON.parse(userRoleDetails);
    setUserRole(userDetails?.roles);
  }, []);

  // Keep dropdowns open based on current route
  useEffect(() => {
    const path = location.pathname;

    if (path.startsWith("/recon")) setReconOpen(true);
    if (path.startsWith("/recon/channel")) {
      setChannelOpen(true);
      const matched = channelItems.find((item) =>
        item.children?.some((child) => path.startsWith(child.to)),
      );
      if (matched) setOpenMenu(matched.label);
    }
    if (path.startsWith("/recon/accounts")) setAccountsOpen(true);
    if (path.startsWith("/settings")) setSettingsOpen(true);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getFullName = () => {
    const parts = [user?.f_name, user?.m_name, user?.l_name].filter(Boolean);
    return parts.join(" ") || user?.username || "User";
  };

  // const checkUserRole = (type:string) => {
  //   console.log('userRole',userRole);
  //   console.log('userRole?.some((role:any) => role.name === type) ?? false',userRole?.some((role:any) => role.name === type) ?? false)
  //   return userRole?.some((role:any) => role.name === type) ?? false
  // }

  const parsePermissionJson = (permissionJson: string): any[] => {
    try {
      return JSON.parse(permissionJson);
    } catch (error) {
      console.error("Invalid permission_json", error);
      return [];
    }
  };

  const getAllModuleNamesFromRoles = (roles: any): string[] => {
    const moduleSet = new Set<string>();

    roles.forEach((role: any) => {
      const permissions = parsePermissionJson(role.permission_json);

      permissions.forEach((perm: any) => {
        if (perm.moduleName) {
          moduleSet.add(perm.moduleName.toLowerCase());
        }
      });
    });

    return Array.from(moduleSet);
  };

  // const checkUserRole = (types: string[]) => {
  //   if (!Array.isArray(userRole)) return false;
  //   const permissions = getAllModuleNamesFromRoles(userRole);
  //   console.log("userRole", userRole);
  //   console.log("permissions", permissions);
  //   return userRole.some((role: any) => types.includes(role.name));
  // };

  const checkUserRole = (modules: any[]) => {
    if (!Array.isArray(userRole)) return false;

    const allowedModules = getAllModuleNamesFromRoles(userRole);
    // console.log('allowedModules',allowedModules);
    if (!Array.isArray(modules)) return false;
    return modules.some((m) => allowedModules.includes(m.toLowerCase()));
  };

  // console.groupCollapsed('userRoleDetails',JSON.parse(userRoleDetails))
  // const isAdmin = roles?.some(role => role.name === "Admin") ?? false;

  return (
    <aside className={`sidebar ${open ? "w-64 lg:w-72" : "w-20"}`}>
      {/* Header */}
      <div className="sidebar__header">
        {open && <p className="sidebar__brand">AI RECON</p>}
        <button
          className="sidebar__toggle"
          aria-label={open ? "Collapse Sidebar" : "Expand Sidebar"}
          onClick={() => setOpen(!open)}
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="sidebar__nav">
        {/* Dashboard & Upload */}
        {nav
          .filter((item) => !item.roles || checkUserRole(item.roles))
          .slice(0, 2) // Dashboard + Upload (or first two after role filter)
          .map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                `sidebar__item ${isActive ? "sidebar__item--active" : ""}`
              }
            >
              <span className="sidebar__item-icon">
                <Icon className="h-6 w-6" />
              </span>
              {open && <span className="sidebar__item-label">{label}</span>}
            </NavLink>
          ))}

        {/* Recon Dropdown */}
        {(checkUserRole(["channels"]) || checkUserRole(["accounts"])) && (
          <div className="flex flex-col">
            <button
              onClick={() => open && setReconOpen(!reconOpen)}
              className="sidebar__section-btn"
            >
              <div
                className={`sidebar__section-row ${
                  isReconActive ? "sidebar__section-row--active" : ""
                }`}
              >
                <span className="sidebar__item-icon">
                  <SearchCheck className="h-6 w-6" />
                </span>
                {open && (
                  <>
                    <span className="sidebar__item-label flex-1 text-left">
                      Recon
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 ${reconOpen ? "rotate-180" : ""}`}
                    />
                  </>
                )}
              </div>
            </button>

            {checkUserRole(["channels"]) && open && reconOpen && (
              <div className="sidebar__submenu">
                {/* Channels */}
                <div className="flex flex-col">
                  <button
                    onClick={() => setChannelOpen(!channelOpen)}
                    className="sidebar__section-btn"
                  >
                    <div
                      className={`sidebar__section-row ${
                        location.pathname.startsWith("/recon/channel")
                          ? "sidebar__section-row--active"
                          : ""
                      }`}
                    >
                      <FileSearch className="h-5 w-5 sidebar__item-icon" />
                      <span className="flex-1 text-left">Channels</span>
                      <ChevronRight
                        className={`h-4 w-4 ${channelOpen ? "rotate-90" : ""}`}
                      />
                    </div>
                  </button>

                  {channelOpen && (
                    <div className="sidebar__submenu">
                      {channelItems.map((item) =>
                        item.children ? (
                          <div key={item.label} className="flex flex-col">
                            <button
                              onClick={() => toggleMenu(item.label)}
                              className="sidebar__section-btn"
                            >
                              <div
                                className={`
                      sidebar__subitem
                      flex items-center
                     ${openMenu === item.label ? "sidebar__subitem--parent-active" : ""}

                        `}
                              >
                                <span className="flex-1 text-left">
                                  {item.label}
                                </span>
                                <ChevronRight
                                  className={`h-4 w-4 ${
                                    openMenu === item.label ? "rotate-90" : ""
                                  }`}
                                />
                              </div>
                            </button>
                            {openMenu === item.label && (
                              <div className="sidebar__submenu">
                                {item.children.map(({ to, label }) => (
                                  <NavLink
                                    key={to}
                                    to={to}
                                    className={({ isActive }) =>
                                      `sidebar__subitem ${
                                        isActive
                                          ? "sidebar__subitem--active"
                                          : ""
                                      }`
                                    }
                                  >
                                    {label}
                                  </NavLink>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            className={({ isActive }) =>
                              `sidebar__subitem ${
                                isActive ? "sidebar__subitem--active" : ""
                              }`
                            }
                          >
                            {item.label}
                          </NavLink>
                        ),
                      )}
                    </div>
                  )}
                </div>

                {/* Accounts */}
                {checkUserRole(["accounts"]) && (
                  <div className="flex flex-col">
                    <button
                      onClick={() => setAccountsOpen(!accountsOpen)}
                      className="sidebar__section-btn"
                    >
                      <div
                        className={`sidebar__section-row ${
                          location.pathname.startsWith("/recon/accounts")
                            ? "sidebar__section-row--active"
                            : ""
                        }`}
                      >
                        <TableProperties className="h-5 w-5 sidebar__item-icon" />
                        <span className="flex-1 text-left">Accounts</span>
                        <ChevronRight
                          className={`h-4 w-4 ${accountsOpen ? "rotate-90" : ""}`}
                        />
                      </div>
                    </button>

                    {accountsOpen && (
                      <div className="sidebar__submenu">
                        {accountItems.map(({ to, label }) => (
                          <NavLink
                            key={to}
                            to={to}
                            className={({ isActive }) =>
                              `sidebar__subitem ${
                                isActive ? "sidebar__subitem--active" : ""
                              }`
                            }
                          >
                            {label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {/* Under Investigation */} 
        {(checkUserRole(["channels"]) || checkUserRole(["accounts"])) && (
          <div className="flex flex-col">
            <button
              onClick={() => open && setInvestigateOpen(!investigateOpen)}
              className="sidebar__section-btn"
            >
              <div
                className={`sidebar__section-row ${
                  isInvestigateActive ? "sidebar__section-row--active" : ""
                }`}
              >
                <span className="sidebar__item-icon">
                  <Search className="h-6 w-6" />
                </span>

                {open && (
                  <>
                    <span className="sidebar__item-label flex-1 text-left">
                      Under Investigation
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 ${investigateOpen ? "rotate-180" : ""}`}
                    />
                  </>
                )}
              </div>
            </button>

            {open && investigateOpen && (
              <div className="sidebar__submenu">
                {investigateItems
                  .filter((item) => checkUserRole(item.roles))
                  .map(({ to, label }) => (
                    <NavLink
                      key={to}
                      to={to}
                      className={({ isActive }) =>
                        `sidebar__subitem ${
                          isActive ? "sidebar__subitem--active" : ""
                        }`
                      }
                    >
                      {label}
                    </NavLink>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Reports */}
        {checkUserRole(["reports"]) && (
          <div className="flex flex-col">
            <button
              onClick={() => open && setReportsOpen(!reportsOpen)}
              className="sidebar__section-btn"
            >
              <div
                className={`sidebar__section-row ${
                  isReportsActive ? "sidebar__section-row--active" : ""
                }`}
              >
                <span className="sidebar__item-icon">
                  <BarChart className="h-6 w-6" />
                </span>

                {open && (
                  <>
                    <span className="sidebar__item-label flex-1 text-left">
                      Reports
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 ${reportsOpen ? "rotate-180" : ""}`}
                    />
                  </>
                )}
              </div>
            </button>

            {open && reportsOpen && (
              <div className="sidebar__submenu">
                {reportsItems.map(({ to, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      `sidebar__subitem ${
                        isActive ? "sidebar__subitem--active" : ""
                      }`
                    }
                  >
                    {label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Other nav items  */}

        {nav
          .filter(
            (item) =>
              item.to !== "/matching-rule" &&
              (!item.roles || checkUserRole(item.roles)),
          )
          .slice(2)
          .map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `sidebar__item ${isActive ? "sidebar__item--active" : ""}`
              }
            >
              <span className="sidebar__item-icon">
                <Icon className="h-6 w-6" />
              </span>
              {open && <span className="sidebar__item-label">{label}</span>}
            </NavLink>
          ))}

        {/* User Managment */}
        {checkUserRole(["system_administration"]) && (
          <div className="flex flex-col">
            <button
              onClick={() => open && setSystemAdminOpen(!systemAdminOpen)}
              className="sidebar__section-btn"
            >
              <div
                className={`sidebar__section-row ${
                  isSettingsActive ? "sidebar__section-row--active" : ""
                }`}
              >
                <span className="sidebar__item-icon">
                  <User className="h-6 w-6" />
                </span>
                {open && (
                  <>
                    <span className="sidebar__item-label flex-1 text-left">
                      System Administration
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 ${
                        systemAdminOpen ? "rotate-180" : ""
                      }`}
                    />
                  </>
                )}
              </div>
            </button>

            {open && systemAdminOpen && (
              <div className="sidebar__submenu">
                {systemAdministration.map(({ to, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      `sidebar__subitem ${
                        isActive ? "sidebar__subitem--active" : ""
                      }`
                    }
                  >
                    {label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings Dropdown */}
        {checkUserRole(["settings"]) && (
          <div className="flex flex-col">
            <button
              onClick={() => open && setSettingsOpen(!settingsOpen)}
              className="sidebar__section-btn"
            >
              <div
                className={`sidebar__section-row ${
                  isSettingsActive ? "sidebar__section-row--active" : ""
                }`}
              >
                <span className="sidebar__item-icon">
                  <Settings className="h-6 w-6" />
                </span>
                {open && (
                  <>
                    <span className="sidebar__item-label flex-1 text-left">
                      Settings
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 ${settingsOpen ? "rotate-180" : ""}`}
                    />
                  </>
                )}
              </div>
            </button>

            {open && settingsOpen && (
              <div className="sidebar__submenu">
                {settingsItems.map(({ to, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      `sidebar__subitem ${
                        isActive ? "sidebar__subitem--active" : ""
                      }`
                    }
                  >
                    {label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* User Profile & Logout */}
      <div className="sidebar__footer">
        <div className={`sidebar__profile ${!open ? "justify-center" : ""}`}>
          <div className="app-avatar ">
            {userEmail ? userEmail.charAt(0).toUpperCase() : "I"}
          </div>
          {open && (
            <div className="flex-1 min-w-0">
              <p className="sidebar__profile-name truncate capitalize">
                {getFullName() || "User"}
              </p>
              <p className="sidebar__profile-email truncate">
                {user?.email || "No email"}
              </p>
            </div>
          )}
        </div>

        <button
          onClick={() => setShowLogoutConfirm(true)}
          className={`sidebar__logout-btn ${!open ? "justify-center" : ""}`}
        >
          <LogOut className="h-5 w-5" />
          {open && <span>Logout</span>}
        </button>
      </div>

      {/* Logout */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirm Logout
            </h3>
            <p className="text-gray-600 text-sm mb-6">
              Are you sure you want to logout from your account?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button onClick={handleLogout} className="btn btn-danger flex-1">
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
